import { call } from "@saas/api-client";
import { Alert, Badge, Button, Card, ErrorAlert, formatDate, Loading, PageHeader, Table, useAsync, useCommand } from "@saas/ui";
import { useEffect, useId, useRef, useState } from "react";
import { api } from "./api";
import { ResyncIdentityProviderCard } from "./ResyncIdentityProviderCard";
import { useTenantNames } from "./useTenantNames";

const firstLine = (text: string | null | undefined) => (text ?? "").split("\n")[0]?.slice(0, 160) ?? "";

export function BackgroundJobsPage() {
  const jobs = useAsync(() => call(api.GET("/api/background-jobs")), [], 5000);
  const tenantName = useTenantNames();
  const command = useCommand();
  const [done, setDone] = useState<string>();
  const [confirmDiscard, setConfirmDiscard] = useState<string>();
  // Keyboard focus follows the swap between Discard and Confirm/Cancel, so it never drops to the page (A17.L1).
  const [focusDiscardOf, setFocusDiscardOf] = useState<string>();
  const confirmButton = useRef<HTMLButtonElement>(null);
  const discardButton = useRef<HTMLButtonElement>(null);
  // After a confirmed discard, successful or not, the row may be gone (now or at the next refresh): focus goes to the
  // result message instead (A18.L2, A19.L11). That message is then read once through focus, not again as a live
  // region (A19.L13).
  const result = useRef<HTMLDivElement>(null);
  const messages = useId();
  const [focusResult, setFocusResult] = useState(0);
  const [quiet, setQuiet] = useState(false);
  useEffect(() => {
    if (confirmDiscard) confirmButton.current?.focus();
  }, [confirmDiscard]);
  useEffect(() => {
    if (focusDiscardOf) discardButton.current?.focus();
  }, [focusDiscardOf]);
  useEffect(() => {
    if (focusResult) result.current?.focus();
  }, [focusResult]);
  const cancelDiscard = (id: string) => {
    setConfirmDiscard(undefined);
    setFocusDiscardOf(id);
  };

  const retry = async (id: string, type: string) => {
    setDone(undefined);
    setQuiet(false);
    if (await command.run(() => call(api.POST("/api/background-jobs/{id}/retry", { params: { path: { id } } })))) {
      setDone(`${type} scheduled to run now.`);
      jobs.reload();
    }
  };

  // For jobs that can never succeed (e.g. an address the mail server rejects for good): closed without running.
  const discard = async (id: string, type: string) => {
    setDone(undefined);
    setConfirmDiscard(undefined);
    setQuiet(true);
    // Failed (e.g. delivered meanwhile, so the row is about to disappear): the error is shown in the same place.
    if (await command.run(() => call(api.POST("/api/background-jobs/{id}/discard", { params: { path: { id } } })))) {
      setDone(`${type} discarded.`);
    }
    setFocusResult((n) => n + 1);
    jobs.reload();
  };

  return (
    <>
      <PageHeader
        title="Background jobs"
        description="Outbox jobs that failed at least once: retrying with back-off, or parked after too many attempts. Delivered and discarded jobs disappear from this list."
      />
      <Card title="Failing and parked jobs">
        <div ref={result} tabIndex={-1} role="group" aria-labelledby={messages} onBlur={() => setQuiet(false)}>
          <div id={messages}>
            <ErrorAlert error={command.error ?? jobs.error} live={!quiet} />
            {done && (
              <Alert tone="success" live={!quiet}>
                {done}
              </Alert>
            )}
          </div>
        </div>
        {jobs.loading && !jobs.data ? (
          <Loading />
        ) : jobs.data?.length === 0 ? (
          <p className="sb-muted">No failing or parked jobs.</p>
        ) : (
          <Table caption={`${jobs.data?.length ?? 0} job(s)`} headers={["Type", "Module", "Tenant", "Attempts", "Status", "Last error", "Next attempt", ""]}>
            {jobs.data?.map((j) => (
              <tr key={j.id} data-job-id={j.id}>
                <td title={j.fullType}>{j.type}</td>
                <td>{j.module}</td>
                <td>{tenantName(j.tenantId)}</td>
                <td>{j.attempts}</td>
                <td>
                  <Badge tone={j.status === "Parked" ? "danger" : "warning"}>{j.status}</Badge>
                </td>
                <td>
                  <details>
                    <summary>{firstLine(j.lastError)}</summary>
                    <pre className="sb-pre">{j.lastError}</pre>
                  </details>
                </td>
                <td>{j.status === "Parked" ? "—" : formatDate(j.nextAttemptAt)}</td>
                <td>
                  <div className="sb-actions">
                    <Button small variant="primary" onClick={() => retry(j.id, j.type)} disabled={command.pending} aria-label={`Retry ${j.type} now`}>
                      Retry now
                    </Button>
                    {confirmDiscard === j.id ? (
                      <>
                        <Button ref={confirmButton} small variant="danger" onClick={() => discard(j.id, j.type)} disabled={command.pending} aria-label={`Confirm discarding ${j.type}`}>
                          Confirm discard
                        </Button>
                        <Button small onClick={() => cancelDiscard(j.id)} aria-label={`Cancel discarding ${j.type}`}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        ref={focusDiscardOf === j.id ? discardButton : undefined}
                        small
                        variant="danger"
                        onClick={() => {
                          setFocusDiscardOf(undefined);
                          setConfirmDiscard(j.id);
                        }}
                        disabled={command.pending}
                        aria-label={`Discard ${j.type}`}
                      >
                        Discard
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <ResyncIdentityProviderCard />
    </>
  );
}
